import { inject, Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, switchMap, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvoiceDataService } from '../invoice-data.service';
import { InvoiceNumberError } from '../types/ITestInvoiceNumberResponse';

@Injectable()
export class InvoiceNumberValidatorService {
    public static readonly invoiceNumberExistsErrorName = 'invoiceNumberExists';
    public static readonly invoiceNumberInvalidFormatErrorName = 'invoiceNumberInvalidFormat';
    public static readonly invoiceNumberInvalid = 'invoiceNumberInvalid';

    private readonly _invoiceDataService: InvoiceDataService = inject(InvoiceDataService);

    /**
     * Creates an async validator that checks if the invoice number is available
     * @param supplierCompanyId The supplier company ID to check against
     * @param excludeInvoiceId Optional invoice ID to exclude from the check
     * @returns An async validator function
     */
    public get(supplierCompanyId: string, excludeInvoiceId?: string): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            const invoiceNumber = control.value;

            if (!invoiceNumber) {
                return of(null); // Don't validate empty values
            }

            // give the service call a debounce time of 300ms to avoid too many calls on every keystroke
            return timer(300).pipe(
                switchMap(() =>
                    this._invoiceDataService.checkIfInvoiceNumberIsAvailable(supplierCompanyId, invoiceNumber, excludeInvoiceId),
                ),
                map((response) => {
                    if (!response.valid) {
                        // Return specific error based on the error type
                        if (response.error.includes(InvoiceNumberError.ALREADY_EXISTS)) {
                            return { [InvoiceNumberValidatorService.invoiceNumberExistsErrorName]: true };
                        } else if (response.error.includes(InvoiceNumberError.INVALID_FORMAT)) {
                            return { [InvoiceNumberValidatorService.invoiceNumberInvalidFormatErrorName]: true };
                        } else {
                            return { [InvoiceNumberValidatorService.invoiceNumberInvalid]: true };
                        }
                    }
                    return null; // Valid
                }),
            );
        };
    }
}
